import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import ErrorBoundary from '../../react/ErrorBoundary.tsx';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  fetchPVHistory,
  fetchBatteryHistory,
  fetchGeneratorHistory,
  fetchGridHistory,
  fetchPvAssetSummary,
  fetchBatteryAssetSummary,
  fetchGeneratorAssetSummary,
  fetchGridAssetSummary,
} from '../../services/api.ts';
import { subscribeDeviceState, isMqttConnected, isMqttRealtimeEnabled } from '../../services/mqttClient.ts';
import { resolveSiteTimezone } from '../../utils/timezone.ts';

const DEFAULT_COLORS = ['#00ff00', '#ff9800', '#2196f3', '#e91e63', '#9c27b0', '#ffc107', '#00bcd4', '#4caf50'];
const CHART_TYPE_OPTIONS = [
  { value: 'lines', label: 'Line' },
  { value: 'lines+markers', label: 'Line + Markers' },
  { value: 'bar', label: 'Bar' },
  { value: 'area', label: 'Area' },
  { value: 'spline', label: 'Spline' },
];
const SUPPORTED_CHART_TYPES = CHART_TYPE_OPTIONS.map((option) => option.value);
const CHART_WARM_CACHE = new Map<string, {
  traces: any[];
  data: any[];
  chartType: string;
  dateRange: { start: string; end: string };
  interval: string | null;
  bindings: any[] | null;
  bindingSignature: string;
  refreshedAt: number;
}>();

function normalizeChartType(chartType) {
  if (SUPPORTED_CHART_TYPES.includes(chartType)) {
    return chartType;
  }
  return 'lines+markers';
}

function normalizeAllowedChartTypes(allowedTypes) {
  const list = Array.isArray(allowedTypes)
    ? allowedTypes
        .map((type) => normalizeChartType(type))
        .filter((type, index, arr) => arr.indexOf(type) === index)
    : [];

  return list.length > 0 ? list : [...SUPPORTED_CHART_TYPES];
}

function resolveChartType(preferredType, allowedTypes) {
  const normalized = normalizeChartType(preferredType);
  if (allowedTypes.includes(normalized)) {
    return normalized;
  }
  return allowedTypes[0] || 'lines+markers';
}

function formatTickLabel(value, timezone = 'UTC') {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return formatter.format(date);
}

function formatTooltipLabel(value, timezone = 'UTC') {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return formatter.format(date);
}

function mapYAxisId(yaxis) {
  return yaxis === 'y2' ? 'right' : 'left';
}

function detectBucketMs(traces): number {
  // Find the smallest consistent gap across all trace data to determine interval.
  let minGap = Infinity;
  traces.forEach((trace) => {
    const data = trace.data || [];
    for (let i = 1; i < data.length; i++) {
      const t1 = data[i - 1].timestamp instanceof Date
        ? data[i - 1].timestamp.getTime()
        : new Date(data[i - 1].timestamp).getTime();
      const t2 = data[i].timestamp instanceof Date
        ? data[i].timestamp.getTime()
        : new Date(data[i].timestamp).getTime();
      const gap = t2 - t1;
      if (gap > 0 && gap < minGap) minGap = gap;
    }
  });
  if (!Number.isFinite(minGap) || minGap === Infinity) return 60_000; // default 1 min
  // Round bucket to a clean interval: 10s, 30s, 1m, 5m, 15m, 30m, 1h
  const clean = [10_000, 30_000, 60_000, 300_000, 900_000, 1_800_000, 3_600_000];
  return clean.find((c) => c >= minGap) ?? 3_600_000;
}

function buildMergedChartData(traces) {
  const bucketMs = detectBucketMs(traces);
  const timeMap = new Map();

  traces.forEach((trace) => {
    (trace.data || []).forEach((point) => {
      const rawTs = point.timestamp instanceof Date
        ? point.timestamp.getTime()
        : new Date(point.timestamp).getTime();

      if (!Number.isFinite(rawTs)) return;

      // Snap to the common bucket so all series align on the same timestamp key.
      // Use floor (not round) so labels never jump into the future bucket.
      const bucketTs = Math.floor(rawTs / bucketMs) * bucketMs;

      const entry = timeMap.get(bucketTs) || { timestamp: bucketTs };
      // Keep the value closest to bucket centre when two points snap to the same bucket.
      const prevRawTs = entry[`__rawTs_${trace.name}`] ?? null;
      if (!(trace.name in entry) || prevRawTs === null || Math.abs(rawTs - bucketTs) < Math.abs(prevRawTs - bucketTs)) {
        entry[trace.name] = point.value;
        entry[`__rawTs_${trace.name}`] = rawTs;
      }
      timeMap.set(bucketTs, entry);
    });
  });

  // Ensure all series have entries for all timestamps (fill missing with null for connectNulls)
  const allTraceNames = new Set<string>();
  traces.forEach((trace) => allTraceNames.add(trace.name));

  const result = Array.from(timeMap.values());
  result.forEach((entry) => {
    allTraceNames.forEach((name) => {
      if (!(name in entry)) entry[name] = null;
      // Clean up internal tracking keys
      delete entry[`__rawTs_${name}`];
    });
  });

  return result.sort((a, b) => a.timestamp - b.timestamp);
}

function cloneTraceData(traces) {
  return (Array.isArray(traces) ? traces : []).map((trace) => ({
    ...trace,
    data: (Array.isArray(trace?.data) ? trace.data : []).map((point) => ({
      ...point,
      timestamp: point?.timestamp instanceof Date ? new Date(point.timestamp.getTime()) : new Date(point?.timestamp),
    })),
  }));
}

// Values must match the API's historyInterval enum: '5m' | '15m' | '30m' | '1h' | '6h' | '12h' | '1d'
const INTERVAL_PRESETS = [
  { label: '5m',  value: '5m' },
  { label: '15m', value: '15m' },
  { label: '30m', value: '30m' },
  { label: '1h',  value: '1h' },
  { label: '6h',  value: '6h' },
  { label: '12h', value: '12h' },
  { label: '1D',  value: '1d' },
];

function autoIntervalForRange(startIso: string, endIso: string): string {
  const rangeMs = new Date(endIso).getTime() - new Date(startIso).getTime();
  const hours = rangeMs / (60 * 60 * 1000);
  if (hours <= 6)   return '5m';
  if (hours <= 24)  return '15m';
  if (hours <= 72)  return '30m';
  if (hours <= 168) return '1h';
  if (hours <= 720) return '6h';
  return '12h';
}

function ChartControls({ chartId, chartState, allowedChartTypes, onApply }) {
  const [chartType, setChartType] = useState(
    resolveChartType(chartState.chartType || 'lines+markers', allowedChartTypes)
  );
  const [startDate, setStartDate] = useState(chartState.dateRange.start.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(chartState.dateRange.end.toISOString().split('T')[0]);
  const [activeInterval, setActiveInterval] = useState<string>(
    chartState.interval ||
    autoIntervalForRange(
      chartState.dateRange.start.toISOString(),
      chartState.dateRange.end.toISOString(),
    )
  );
  const showTypeControl = !chartState?.layout?.typeControlsInHeader;
  const showDateRangeOnlyWhenFullscreen = Boolean(chartState?.layout?.showDateRangeOnlyWhenFullscreen);
  const showIntervalPresets = !chartState?.layout?.rangeControlsInHeader;

  useEffect(() => {
    const startIso = chartState.dateRange.start.toISOString();
    const endIso = chartState.dateRange.end.toISOString();
    setStartDate(startIso.split('T')[0]);
    setEndDate(endIso.split('T')[0]);
    if (chartState.interval) {
      setActiveInterval(chartState.interval);
    }
  }, [chartState.dateRange.start, chartState.dateRange.end, chartState.interval]);

  return (
    <div className="recharts-chart-controls" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
      {showTypeControl && (
        <>
          <label htmlFor={`${chartId}-type`} style={{ fontSize: '0.75rem' }}>Type</label>
          <div style={{ position: 'relative', display: 'inline-block' }} className="custom-select-wrapper">
            <button
              type="button"
              id={`${chartId}-type`}
              className="custom-select-btn"
              disabled={allowedChartTypes.length <= 1}
              onClick={(e) => {
                const btn = e.currentTarget;
                const menu = btn.nextElementSibling as HTMLDivElement;
                if (menu) menu.style.display = menu.style.display === 'none' ? 'block' : 'none';
              }}
              onBlur={(e) => {
                setTimeout(() => {
                  const menu = (e.currentTarget.nextElementSibling as HTMLDivElement);
                  if (menu && !menu.matches(':hover')) menu.style.display = 'none';
                }, 100);
              }}
              style={{
                appearance: 'none',
                WebkitAppearance: 'none',
                padding: '4px 24px 4px 8px',
                fontSize: '0.75rem',
                cursor: allowedChartTypes.length <= 1 ? 'not-allowed' : 'pointer',
                border: '1px solid var(--chart-controls-select-border)',
                borderRadius: '6px',
                backgroundColor: 'var(--chart-controls-select-bg)',
                color: 'var(--chart-controls-select-text)',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${encodeURIComponent('var(--chart-controls-select-text)')}' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 6px center',
              }}
            >
              {CHART_TYPE_OPTIONS.find((opt) => opt.value === chartType)?.label || 'Select type'}
            </button>
            <div
              className="custom-select-menu"
              style={{
                display: 'none',
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 1000,
                minWidth: '150px',
                backgroundColor: 'var(--chart-controls-select-bg)',
                border: '1px solid var(--chart-controls-select-border)',
                borderRadius: '6px',
                marginTop: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {CHART_TYPE_OPTIONS.filter((option) => allowedChartTypes.includes(option.value)).map((option) => (
                <div
                  key={option.value}
                  onClick={() => {
                    setChartType(option.value);
                    const btn = document.getElementById(`${chartId}-type`) as HTMLButtonElement;
                    if (btn) {
                      btn.textContent = option.label;
                      const menu = btn.nextElementSibling as HTMLDivElement;
                      if (menu) menu.style.display = 'none';
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    color: 'var(--chart-controls-select-text)',
                    backgroundColor: chartType === option.value ? 'var(--chart-controls-select-bg)' : 'transparent',
                    opacity: chartType === option.value ? 0.8 : 1,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = chartType === option.value ? 'var(--chart-controls-select-bg)' : 'transparent';
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <div className={showDateRangeOnlyWhenFullscreen ? 'chart-controls-date-range chart-controls-date-range--fullscreen-only' : 'chart-controls-date-range'}>
        {showIntervalPresets && (
          <div className="chart-controls-presets">
            {INTERVAL_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className={`chart-preset-btn${activeInterval === preset.value ? ' active' : ''}`}
                onClick={() => {
                  setActiveInterval(preset.value);
                  // Keep full timestamp precision for interval toggles.
                  // Using date-only values here can truncate range to midnight and fetch stale windows.
                  onApply(
                    chartType,
                    chartState?.dateRange?.start?.toISOString?.() || startDate,
                    chartState?.dateRange?.end?.toISOString?.() || endDate,
                    preset.value,
                  );
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        )}
        <label htmlFor={`${chartId}-start`} style={{ fontSize: '0.75rem' }}>From</label>
        <input
          id={`${chartId}-start`}
          type="date"
          value={startDate}
          onChange={(event) => {
            setStartDate(event.target.value);
          }}
        />
        <label htmlFor={`${chartId}-end`} style={{ fontSize: '0.75rem' }}>To</label>
        <input
          id={`${chartId}-end`}
          type="date"
          value={endDate}
          onChange={(event) => {
            setEndDate(event.target.value);
          }}
        />
        <button
          type="button"
          onClick={() => {
            onApply(chartType, startDate, endDate, activeInterval);
          }}
        >
          Update
        </button>
      </div>
    </div>
  );
}

function parseDateInputBoundary(value: string, boundary: 'start' | 'end'): Date {
  const raw = String(value || '').trim();
  const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (dateOnlyMatch) {
    const suffix = boundary === 'end' ? 'T23:59:59.999' : 'T00:00:00.000';
    const parsed = new Date(`${raw}${suffix}`);
    return Number.isFinite(parsed.getTime()) ? parsed : new Date();
  }

  const parsed = new Date(raw);
  return Number.isFinite(parsed.getTime()) ? parsed : new Date();
}

function RechartsChartComponent({ chartId, chartState, onChartTypeChange, onDateRangeChange }: {
  chartId: string;
  chartState: any;
  onChartTypeChange?: (type: string) => void;
  onDateRangeChange?: (start: Date, end: Date, interval?: string) => void;
}) {
  const fullData = Array.isArray(chartState?.data) ? chartState.data : [];
  const dataLength = fullData.length;
  const [viewStartIndex, setViewStartIndex] = useState(0);
  const [viewWindowSize, setViewWindowSize] = useState(120);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const navigatorRef = useRef<HTMLDivElement | null>(null);
  const panDragRef = useRef({ active: false, startX: 0, startIndex: 0 });
  const [isFullscreenPanel, setIsFullscreenPanel] = useState(false);
  const [hiddenSeries, setHiddenSeries] = useState(new Set<string>());

  useEffect(() => {
    const nextWindow = Math.min(120, Math.max(20, dataLength || 0));
    const maxStart = Math.max(0, dataLength - nextWindow);
    setViewWindowSize(nextWindow || 120);
    setViewStartIndex(maxStart);
  }, [dataLength]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const panel = wrapper?.closest('.dashboard-panel');
    if (!panel) {
      setIsFullscreenPanel(false);
      return undefined;
    }

    const syncFullscreenState = () => {
      setIsFullscreenPanel(panel.classList.contains('dashboard-chart-focus'));
    };

    syncFullscreenState();

    const observer = new MutationObserver(syncFullscreenState);
    observer.observe(panel, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, [chartId]);

  const effectiveWindowSize = Math.min(Math.max(20, viewWindowSize), Math.max(1, dataLength));
  const maxStartIndex = Math.max(0, dataLength - effectiveWindowSize);
  const clampedStartIndex = Math.min(Math.max(0, viewStartIndex), maxStartIndex);
  const clampedEndIndex = Math.min(dataLength - 1, clampedStartIndex + effectiveWindowSize - 1);
  const visibleData = useMemo(
    () => fullData.slice(clampedStartIndex, clampedEndIndex + 1),
    [fullData, clampedStartIndex, clampedEndIndex]
  );

  const hasRightAxis = useMemo(
    () => (chartState.traces || []).some((trace) => mapYAxisId(trace.yaxis) === 'right'),
    [chartState.traces]
  );

  const allowedChartTypes = useMemo(
    () => normalizeAllowedChartTypes(chartState.allowedChartTypes || chartState.layout?.allowedChartTypes),
    [chartState.allowedChartTypes, chartState.layout?.allowedChartTypes]
  );
  const normalizedType = resolveChartType(
    chartState.chartType || chartState.layout.type || 'lines+markers',
    allowedChartTypes
  );
  const layoutHeight = chartState?.layout?.height;
  const height = typeof layoutHeight === 'string'
    ? layoutHeight
    : `${Number(layoutHeight) || 300}px`;
  const minHeight = typeof layoutHeight === 'string'
    ? '0'
    : `${Number(layoutHeight) || 300}px`;
  const showControls = Boolean(chartState?.layout?.showControls);
  const tickColor = 'var(--chart-axis-tick, #1f2937)';
  const axisLabelColor = 'var(--chart-axis-label, #111827)';
  const gridStroke = 'var(--chart-grid-stroke, rgba(15, 23, 42, 0.32))';
  const tooltipBg = 'var(--chart-tooltip-bg, #ffffff)';
  const tooltipBorder = 'var(--chart-tooltip-border, #cbd5e1)';
  const tooltipText = 'var(--chart-tooltip-text, #0f172a)';
  const legendActiveColor = 'var(--chart-legend-active, #0f172a)';
  const legendDisabledColor = 'var(--chart-legend-disabled, #6b7280)';
  const cursorStroke = 'var(--chart-tooltip-cursor, rgba(15, 23, 42, 0.35))';
  const canPanByDrag = dataLength > effectiveWindowSize;
  const panOnlyWhenFullscreen = Boolean(chartState?.layout?.showNavigatorOnlyWhenFullscreen);
  const hasNavigatorData = dataLength > 1;
  const showGlassNavigator = hasNavigatorData && (!panOnlyWhenFullscreen || isFullscreenPanel);
  const canPanNow = showGlassNavigator && canPanByDrag;
  const siteTimezone = chartState?.siteTimezone || 'UTC';

  const glassWindowLeftPct = dataLength > 0 ? (clampedStartIndex / dataLength) * 100 : 0;
  const glassWindowWidthPct = dataLength > 0 ? (effectiveWindowSize / dataLength) * 100 : 100;

  const onPanPointerDown = (event) => {
    if (!canPanNow) return;
    panDragRef.current = {
      active: true,
      startX: event.clientX,
      startIndex: clampedStartIndex,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPanPointerMove = (event) => {
    const drag = panDragRef.current;
    if (!drag.active || !canPanNow) return;

    const width = Math.max(1, navigatorRef.current?.clientWidth || 1);
    const deltaX = event.clientX - drag.startX;
    const pointsPerPixel = effectiveWindowSize / width;
    const indexDelta = Math.round(-deltaX * pointsPerPixel);
    const nextStart = Math.max(0, Math.min(maxStartIndex, drag.startIndex + indexDelta));

    if (nextStart !== clampedStartIndex) {
      setViewStartIndex(nextStart);
    }
  };

  const onPanPointerUp = (event) => {
    panDragRef.current.active = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const disabledTraceNames = useMemo(() => {
    const disabled = new Set();
    (chartState.traces || []).forEach((trace) => {
      const hasData = Array.isArray(trace?.data) && trace.data.some((point) => Number.isFinite(Number(point?.value)));
      if (!hasData) {
        disabled.add(trace?.name);
      }
    });
    return disabled;
  }, [chartState.traces]);

  const legendPayload = useMemo(
    () => (chartState.traces || []).map((trace, index) => {
      const color = trace.stroke || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
      const isDisabled = disabledTraceNames.has(trace?.name);
      return {
        id: trace?.name || `series-${index}`,
        value: trace?.name || `Series ${index + 1}`,
        type: 'line',
        color: isDisabled ? 'rgba(100, 116, 139, 0.55)' : color,
        inactive: isDisabled,
      };
    }),
    [chartState.traces, disabledTraceNames]
  );

  const renderSeries = (isMini = false) => (chartState.traces || []).map((trace, index) => {
    const color = trace.stroke || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
    const isHidden = hiddenSeries.has(trace.name);
    const isDisabled = disabledTraceNames.has(trace.name);

    if (isDisabled) {
      return null;
    }

    const seriesOpacity = isHidden ? 0.15 : (isMini ? 0.28 : 1);
    const strokeWidth = isMini ? 1.25 : 2;
    const seriesKey = `${trace.name}-${index}`;
    const commonProps = {
      dataKey: trace.name,
      stroke: color,
      fill: color,
      name: trace.name,
      yAxisId: mapYAxisId(trace.yaxis),
      isAnimationActive: false,
      opacity: isHidden ? 0.15 : 1,
    };

    if (normalizedType === 'bar') {
      return <Bar key={seriesKey} {...commonProps} fillOpacity={isHidden ? 0.08 : (isMini ? 0.22 : 0.85)} strokeOpacity={seriesOpacity} />;
    }

    if (normalizedType === 'area') {
      return <Area key={seriesKey} {...commonProps} type="monotone" fillOpacity={isHidden ? 0.05 : (isMini ? 0.12 : 0.2)} strokeOpacity={seriesOpacity} strokeWidth={strokeWidth} dot={false} connectNulls={true} />;
    }

    return (
      <Line
        key={seriesKey}
        {...commonProps}
        type={normalizedType === 'spline' ? 'basis' : 'monotone'}
        strokeWidth={strokeWidth}
        dot={normalizedType === 'lines+markers'}
        connectNulls={true}
      />
    );
  });

  return (
    <div ref={wrapperRef} style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {showControls && (
        <ChartControls
          chartId={chartId}
          chartState={chartState}
          allowedChartTypes={allowedChartTypes}
          onApply={(nextType, startDate, endDate, interval) => {
            onChartTypeChange?.(resolveChartType(nextType, allowedChartTypes));
            onDateRangeChange?.(
              parseDateInputBoundary(startDate, 'start'),
              parseDateInputBoundary(endDate, 'end'),
              interval,
            );
          }}
        />
      )}

      {showGlassNavigator && (
        <div
          ref={navigatorRef}
          style={{
            position: 'relative',
            width: '100%',
            height: 56,
            minHeight: 56,
            marginBottom: '0.4rem',
            touchAction: 'none',
            cursor: canPanNow ? (panDragRef.current.active ? 'grabbing' : 'grab') : 'default',
            borderRadius: 12,
            overflow: 'hidden',
            border: '1px solid var(--chart-navigator-border, rgba(148,163,184,0.28))',
            background: 'var(--chart-navigator-bg, rgba(15,23,42,0.02))',
          }}
          onPointerDown={onPanPointerDown}
          onPointerMove={onPanPointerMove}
          onPointerUp={onPanPointerUp}
          onPointerCancel={onPanPointerUp}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={fullData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-navigator-grid-stroke, rgba(148,163,184,0.18))" />
              <XAxis dataKey="timestamp" hide />
              <YAxis hide />
              {renderSeries(true)}
            </ComposedChart>
          </ResponsiveContainer>

          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${glassWindowLeftPct}%`,
              width: `${glassWindowWidthPct}%`,
              minWidth: 12,
              border: '1px solid var(--chart-navigator-window-border, rgba(255,255,255,0.9))',
              borderRadius: 10,
              background: 'var(--chart-navigator-window-bg, rgba(255,255,255,0.10))',
              boxShadow: '0 0 0 9999px var(--chart-navigator-window-shadow, rgba(15,23,42,0.32))',
              backdropFilter: 'var(--chart-navigator-window-backdrop, saturate(1.2) blur(2px))',
              pointerEvents: 'none',
            }}
          />
        </div>
      )}

      <div style={{ width: '100%', height, minHeight, flex: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={visibleData} margin={{ top: 6, right: 16, bottom: 4, left: 48 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey="timestamp"
              minTickGap={24}
              tick={{ fontSize: 8, fill: tickColor }}
              tickFormatter={(value) => formatTickLabel(value, siteTimezone)}
            />
            <YAxis
              yAxisId="left"
                            width={40}
              tick={{ fontSize: 8, fill: tickColor }}
              label={chartState?.layout?.yaxis?.title ? { value: chartState.layout.yaxis.title, angle: -90, position: 'insideLeft', offset: -8, style: { fill: axisLabelColor, fontSize: 8 } } : undefined}
            />
            {hasRightAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                                width={40}
                tick={{ fontSize: 8, fill: tickColor }}
                label={chartState?.layout?.yaxis2?.title ? { value: chartState.layout.yaxis2.title, angle: 90, position: 'insideRight', offset: -8, style: { fill: axisLabelColor, fontSize: 8 } } : undefined}
              />
            )}
            <Tooltip
              labelFormatter={(value) => formatTooltipLabel(value, siteTimezone)}
              contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 8, color: tooltipText, fontSize: 10 }}
              labelStyle={{ color: tooltipText, fontWeight: 700 }}
              itemStyle={{ color: tooltipText }}
              cursor={{ stroke: cursorStroke, strokeWidth: 1 }}
            />
            {renderSeries(false)}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px 12px',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        marginTop: '6px',
        fontSize: 9,
      }}>
        {legendPayload.map((entry) => {
          const seriesName = String(entry?.value || '');
          const isDisabled = disabledTraceNames.has(seriesName);
          const isHidden = hiddenSeries.has(seriesName);
          return (
            <button
              key={String(entry?.id || seriesName)}
              type="button"
              onClick={() => {
                if (!seriesName || isDisabled) return;
                setHiddenSeries((prev) => {
                  const next = new Set(prev);
                  if (next.has(seriesName)) next.delete(seriesName);
                  else next.add(seriesName);
                  return next;
                });
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                border: 'none',
                background: 'transparent',
                padding: 0,
                margin: 0,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.45 : (isHidden ? 0.5 : 1),
                color: isDisabled ? legendDisabledColor : legendActiveColor,
                textDecoration: isDisabled || isHidden ? 'line-through' : 'none',
                transition: 'opacity 0.2s ease',
              }}
              title={isDisabled ? 'Reading not available' : (isHidden ? 'Show series' : 'Hide series')}
            >
              <span style={{
                width: 10,
                height: 2,
                borderRadius: 2,
                display: 'inline-block',
                background: String(entry?.color || '#94a3b8'),
              }} />
              <span>{seriesName}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export class RechartsChartManager {
  site: any;
  registers: any[];
  charts: Record<string, any>;
  roots: Map<string, any>;
  liveSubscriptions: Map<string, () => void>;
  liveRegisterCache: Map<string, any>;
  historyRequestCache: Map<string, { expiresAt: number; data?: any; promise?: Promise<any> }>;
  summaryRequestCache: Map<string, { expiresAt: number; data?: any; promise?: Promise<any> }>;
  historyLoadChain: Promise<void>;

  constructor(site, registers) {
    this.site = site;
    this.registers = registers || [];
    this.charts = {};
    this.roots = new Map();
    this.liveSubscriptions = new Map();
    this.liveRegisterCache = new Map();
    this.historyRequestCache = new Map();
    this.summaryRequestCache = new Map();
    this.historyLoadChain = Promise.resolve();
  }

  _warmKey(containerId: string) {
    return `${String(this.site?._id || 'site')}:${containerId}`;
  }

  _bindingSignature(bindings: any[] = []) {
    return JSON.stringify((Array.isArray(bindings) ? bindings : []).map((binding) => ({
      assetType: binding?.assetType || null,
      metricKey: binding?.metricKey || null,
      name: binding?.name || null,
      yaxis: binding?.yaxis || 'y',
      unit: binding?.unit || null,
      mode: binding?.mode || null,
      color: binding?.line?.color || null,
    })));
  }

  _getWarmSnapshot(containerId: string) {
    return CHART_WARM_CACHE.get(this._warmKey(containerId)) || null;
  }

  _saveWarmSnapshot(containerId: string) {
    const chart = this.charts[containerId];
    if (!chart) return;

    CHART_WARM_CACHE.set(this._warmKey(containerId), {
      traces: cloneTraceData(chart.traces || []),
      data: Array.isArray(chart.data) ? [...chart.data] : [],
      chartType: chart.chartType || 'lines+markers',
      dateRange: {
        start: (chart.dateRange?.start || new Date(Date.now() - 48 * 3600 * 1000)).toISOString(),
        end: (chart.dateRange?.end || new Date()).toISOString(),
      },
      interval: chart.interval || null,
      bindings: Array.isArray(chart.bindings) ? [...chart.bindings] : null,
      bindingSignature: String(chart.bindingSignature || ''),
      refreshedAt: Date.now(),
    });
  }

  createChart(containerId: string, traces: any[] = [], layout: Record<string, any> = {}, parent: Document | HTMLElement = document) {
    const host = parent?.querySelector?.(`#${containerId}`) || document.querySelector(`#${containerId}`);
    if (!host) {
      console.warn(`Chart container #${containerId} not found.`);
      return null;
    }

    let mountNode = host.querySelector?.('.recharts-chart-root') as HTMLElement;
    if (!mountNode) {
      host.innerHTML = '';
      mountNode = document.createElement('div') as HTMLElement;
      mountNode.className = 'recharts-chart-root';
      mountNode.style.width = '100%';
      mountNode.style.height = '100%';
      host.appendChild(mountNode);
    }

    if (!this.roots.has(containerId)) {
      this.roots.set(containerId, createRoot(mountNode));
    }

    const warm = this._getWarmSnapshot(containerId);
    const fallbackDurationMs = 48 * 3600 * 1000;
    const nowMs = Date.now();

    let resolvedDateRange = {
      start: new Date(nowMs - fallbackDurationMs),
      end: new Date(nowMs),
    };

    if (warm?.dateRange) {
      const warmStart = new Date(warm.dateRange.start);
      const warmEnd = new Date(warm.dateRange.end);
      const warmStartMs = warmStart.getTime();
      const warmEndMs = warmEnd.getTime();

      if (Number.isFinite(warmStartMs) && Number.isFinite(warmEndMs) && warmEndMs > warmStartMs) {
        const durationMs = Math.max(5 * 60 * 1000, warmEndMs - warmStartMs);
        const staleToleranceMs = 2 * 60 * 1000;
        const isStaleOrFuture = warmEndMs < (nowMs - staleToleranceMs) || warmEndMs > (nowMs + staleToleranceMs);

        if (isStaleOrFuture) {
          resolvedDateRange = {
            start: new Date(nowMs - durationMs),
            end: new Date(nowMs),
          };
        } else {
          resolvedDateRange = {
            start: warmStart,
            end: warmEnd,
          };
        }
      }
    }

    const warmTraces = warm?.traces ? cloneTraceData(warm.traces) : (traces || []);

    this.charts[containerId] = {
      id: containerId,
      host,
      siteTimezone: resolveSiteTimezone(this.site),
      allowedChartTypes: normalizeAllowedChartTypes(layout.allowedChartTypes),
      layout: {
        title: layout.title || '',
        type: normalizeChartType(layout.type || 'lines+markers'),
        showControls: Boolean(layout.showControls),
        ...layout,
      },
      traces: warmTraces,
      data: warm?.data?.length ? [...warm.data] : buildMergedChartData(warmTraces),
      chartType: resolveChartType(
        warm?.chartType || layout.type || 'lines+markers',
        normalizeAllowedChartTypes(layout.allowedChartTypes)
      ),
      dateRange: resolvedDateRange,
      interval: warm?.interval || autoIntervalForRange(
        resolvedDateRange.start.toISOString(),
        resolvedDateRange.end.toISOString(),
      ),
      bindings: warm?.bindings || null,
      bindingSignature: warm?.bindingSignature || '',
    };

    this._renderChart(containerId);
    return { id: containerId };
  }

  async bindRegisterToChart(chartContainerId, bindings = []) {
    const chart = this.charts[chartContainerId];
    if (!chart) return;

    this._teardownLiveSubscription(chartContainerId);

    const bindingSignature = this._bindingSignature(bindings);
    chart.bindings = bindings;
    chart.bindingSignature = bindingSignature;
    this._setupLiveSubscription(chartContainerId);

    const warm = this._getWarmSnapshot(chartContainerId);
    const canUseWarm = Boolean(
      warm
      && warm.bindingSignature
      && warm.bindingSignature === bindingSignature
      && Array.isArray(warm.traces)
      && warm.traces.length > 0
    );

    if (canUseWarm) {
      chart.traces = cloneTraceData(warm?.traces || []);
      chart.data = warm?.data?.length ? [...warm.data] : buildMergedChartData(chart.traces);
      this._renderChart(chartContainerId);

      // Keep charts visually warm on revisit, but always refresh from API to keep
      // historian-backed data accurate on page load/reload.
    }

    const range = chart.dateRange;
    const traces = [];

    // Asset-history only. Legacy register/configKey bindings are intentionally ignored.
    const assetBindings = bindings.filter((b) => b?.assetType && b?.metricKey);

    // Fetch asset history data if present
    const assetHistoryData = {};
    if (assetBindings.length > 0) {
      const assets = new Set(assetBindings.map((b) => b.assetType));
      const from = range.start.toISOString();
      const to = range.end.toISOString();

      const chartInterval = chart.interval || autoIntervalForRange(from, to);
      for (const assetType of assets) {
        try {
          assetHistoryData[assetType] = await this._getAssetHistory(assetType, from, to, chartInterval);
        } catch (error) {
          console.error(`RechartsChartManager: error fetching ${assetType} history`, error);
        }
      }
    }

    // Process asset history bindings
    for (const binding of assetBindings) {
      const historyData = assetHistoryData[binding.assetType];
      const fallbackPoint = this._getFallbackPointFromRegisters(binding.assetType, binding.metricKey);
      if (!historyData || !historyData.series || !historyData.series[binding.metricKey]) {
        console.warn(
          `RechartsChartManager: asset history data not found for ${binding.assetType}.${binding.metricKey}`
        );
        traces.push({
          name: binding.name || this._humanize(binding.metricKey),
          data: fallbackPoint ? [fallbackPoint] : [],
          stroke: binding.line?.color || DEFAULT_COLORS[traces.length % DEFAULT_COLORS.length],
          yaxis: binding.yaxis || 'y',
          unit: binding.unit || '',
        });
        continue;
      }

      const values = historyData.series[binding.metricKey];
      const timestamps = historyData.timestamps;
      const data = timestamps
        .map((timestamp, index) => ({
          timestamp: new Date(timestamp),
          value: values[index],
        }))
        .filter((point) => Number.isFinite(point.value));

      const resolvedData = data.length > 0
        ? data
        : (fallbackPoint ? [fallbackPoint] : []);

      traces.push({
        name: binding.name || this._humanize(binding.metricKey),
        data: resolvedData,
        stroke: binding.line?.color || DEFAULT_COLORS[traces.length % DEFAULT_COLORS.length],
        yaxis: binding.yaxis || 'y',
        unit: binding.unit || '',
      });
    }

    chart.traces = traces;
    chart.data = buildMergedChartData(traces);
    this._renderChart(chartContainerId);
    this._saveWarmSnapshot(chartContainerId);
  }

  _renderChart(containerId) {
    const chart = this.charts[containerId];
    const root = this.roots.get(containerId);
    if (!chart || !root) return;

    root.render(
      <ErrorBoundary>
        <RechartsChartComponent
          chartId={containerId}
          chartState={chart}
          onChartTypeChange={(chartType) => this._changeChartType(containerId, chartType)}
          onDateRangeChange={(start, end, interval) => this._updateDateRange(containerId, start, end, interval)}
        />
      </ErrorBoundary>
    );
  }

  _changeChartType(containerId, chartType) {
    const chart = this.charts[containerId];
    if (!chart) return;
    chart.chartType = resolveChartType(chartType, normalizeAllowedChartTypes(chart.allowedChartTypes));
    this._renderChart(containerId);
    this._saveWarmSnapshot(containerId);
  }

  setChartType(containerId, chartType) {
    this._changeChartType(containerId, chartType);
  }

  setInterval(containerId: string, interval: string) {
    const chart = this.charts[containerId];
    if (!chart) return;
    let { start, end } = chart.dateRange;

    // For live charts, keep the selected duration but anchor the range end to now
    // when changing interval so API refetch always includes the latest historian data.
    if (this.liveSubscriptions.has(containerId)) {
      const now = new Date();
      const durationMs = Math.max(5 * 60 * 1000, end.getTime() - start.getTime());
      end = now;
      start = new Date(now.getTime() - durationMs);
    }

    this._updateDateRange(containerId, start, end, interval);
  }

  _updateDateRange(containerId, start: Date, end: Date, interval?: string) {
    const chart = this.charts[containerId];
    if (!chart) return;

    const currentRange = chart.dateRange;

    // Shared safeguard for all pages (dashboard, PV, BESS, etc):
    // when only interval is toggled on a live chart, keep the same duration
    // but anchor the query window end to now to avoid stale historical windows.
    if (interval && this.liveSubscriptions.has(containerId) && currentRange?.end instanceof Date) {
      const unchangedEnd = Math.abs(end.getTime() - currentRange.end.getTime()) <= 1000;
      if (unchangedEnd) {
        const now = new Date();
        const durationMs = Math.max(5 * 60 * 1000, end.getTime() - start.getTime());
        end = now;
        start = new Date(now.getTime() - durationMs);
      }
    }
    
    const oldInterval = chart.interval;
    chart.dateRange = { start, end };
    if (interval) {
      chart.interval = interval;
    } else {
      // Keep existing interval or auto-select if not set
      chart.interval = chart.interval || autoIntervalForRange(start.toISOString(), end.toISOString());
    }
    
    const intervalChanged = oldInterval !== chart.interval;
    this.historyRequestCache.clear();
    // Delete warm snapshot when interval changes — do NOT re-save it here with stale data,
    // because bindRegisterToChart would see the new interval on the old data and return early.
    if (intervalChanged) {
      CHART_WARM_CACHE.delete(this._warmKey(containerId));
    } else {
      this._saveWarmSnapshot(containerId);
    }

    if (chart.bindings) {
      void this.bindRegisterToChart(containerId, chart.bindings);
    }
  }

  _humanize(value) {
    return String(value || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  _normalizeKey(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  _assetHints(assetType) {
    switch (assetType) {
      case 'pv':
        return ['pv', 'solar', 'inverter', 'inv'];
      case 'battery':
        return ['battery', 'bess', 'storage', 'bat'];
      case 'generator':
        return ['generator', 'genset', 'dg'];
      case 'grid':
        return ['grid', 'meter', 'utility', 'met'];
      default:
        return [];
    }
  }

  _getRegisterDictionaryKeys(assetType: string, metricKey: string): { keys: string[]; mode: 'sum' | 'avg' | 'latest' } {
    const normalizedMetric = this._normalizeKey(metricKey);

    const map: Record<string, Record<string, { keys: string[]; mode: 'sum' | 'avg' | 'latest' }>> = {
      pv: {
        activepower:       { keys: ['INV_ACTIVE_POWER', 'INV_ACTIVE_POWER_AC'], mode: 'sum' },
        totaloutput:       { keys: ['INV_ACTIVE_POWER', 'INV_ACTIVE_POWER_AC'], mode: 'sum' },
        reactivepower:     { keys: ['INV_REACTIVE_POWER'], mode: 'sum' },
        dailyenergy:       { keys: ['INV_ENERGY_TODAY', 'INV_DAILY_ENERGY', 'PV_ENERGY_TODAY', 'KPI_PV_DAILY_ENERGY'], mode: 'sum' },
        totalenergy:       { keys: ['INV_ENERGY_TOTAL', 'INV_TOTAL_ENERGY', 'PV_ENERGY_TOTAL'], mode: 'sum' },
        availability:      { keys: ['INV_AVAILABILITY', 'PV_AVAILABILITY', 'KPI_PV_AVAILABILITY_PCT_24H'], mode: 'avg' },
        irradiance:        { keys: ['MET_POA_IRRADIANCE', 'MET_GHI', 'MET_IRRADIANCE'], mode: 'latest' },
        moduletemperature: { keys: ['MET_MODULE_TEMP', 'INV_MODULE_TEMP'], mode: 'avg' },
      },
      battery: {
        soc:               { keys: ['BESS_SOC'], mode: 'avg' },
        averagesoc:        { keys: ['BESS_SOC'], mode: 'avg' },
        batterypower:      { keys: ['BESS_ACTIVE_POWER'], mode: 'sum' },
        activepower:       { keys: ['BESS_ACTIVE_POWER'], mode: 'sum' },
        reactivepower:     { keys: ['BESS_REACTIVE_POWER'], mode: 'sum' },
        chargepower:       { keys: ['BESS_CHARGE_POWER', 'BESS_AVAIL_CHARGE_POWER'], mode: 'sum' },
        dischargepower:    { keys: ['BESS_DISCHARGE_POWER', 'BESS_AVAIL_DISCHARGE_POWER'], mode: 'sum' },
        averagetemperature:{ keys: ['BESS_TEMPERATURE', 'BESS_RACK_TEMP'], mode: 'avg' },
      },
      generator: {
        totaloutput:          { keys: ['DG_ACTIVE_POWER'], mode: 'sum' },
        activepower:          { keys: ['DG_ACTIVE_POWER'], mode: 'sum' },
        fuellevel:            { keys: ['DG_FUEL_LEVEL'], mode: 'avg' },
        rpm:                  { keys: ['DG_RPM', 'DG_ENGINE_SPEED'], mode: 'avg' },
        oilpressure:          { keys: ['DG_OIL_PRESSURE'], mode: 'avg' },
        coolanttemperature:   { keys: ['DG_COOLANT_TEMP'], mode: 'avg' },
      },
      grid: {
        activepower:  { keys: ['GRID_ACTIVE_POWER'], mode: 'sum' },
        importpower:  { keys: ['GRID_IMPORT_POWER'], mode: 'sum' },
        exportpower:  { keys: ['GRID_EXPORT_POWER'], mode: 'sum' },
        frequency:    { keys: ['GRID_FREQUENCY'], mode: 'avg' },
        voltage:      { keys: ['GRID_VOLTAGE'], mode: 'avg' },
        powerfactor:  { keys: ['GRID_POWER_FACTOR'], mode: 'avg' },
      },
    };

    const entry = map?.[assetType]?.[normalizedMetric];
    if (entry) return entry;

    // Fallback: treat the metricKey itself as a dictionary key with 'latest' mode
    return { keys: [metricKey], mode: 'latest' };
  }

  _registerBelongsToAsset(register, assetType) {
    const hints = this._assetHints(assetType);
    if (!hints.length) return true;

    const haystack = this._normalizeKey([
      register?.assetType,
      register?.deviceType,
      register?.deviceName,
      register?.name,
    ].filter(Boolean).join(' '));

    if (!haystack) return true;
    return hints.some((hint) => haystack.includes(this._normalizeKey(hint)));
  }

  _extractRegisterNumericValue(register) {
    const raw = register?.value ?? register?.lastValue ?? register?.reading?.value;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
  }

  _getFallbackPointFromRegisters(assetType, metricKey) {
    const { keys: dictKeys } = this._getRegisterDictionaryKeys(assetType, metricKey);
    const dictKeySet = new Set(dictKeys.map((key) => String(key || '').toUpperCase()));
    const candidates = this.registers || [];

    const match = candidates.find((register) => {
      const dk = String(register?.dictionaryKey || '').toUpperCase();
      return dictKeySet.has(dk);
    });

    if (!match) return null;
    const value = this._extractRegisterNumericValue(match);
    if (!Number.isFinite(value)) return null;

    const timestampRaw = match?.updatedAt || match?.timestamp || match?.lastSeen;
    const timestamp = timestampRaw ? new Date(timestampRaw) : new Date();

    return {
      timestamp: Number.isFinite(timestamp.getTime()) ? timestamp : new Date(),
      value,
    };
  }

  _extractReadingsFromStatePayload(payload) {
    if (Array.isArray(payload?.readings)) return payload.readings;
    return [];
  }

  _delay(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  _normalizeHistoryIso(value) {
    const parsed = new Date(value);
    if (!Number.isFinite(parsed.getTime())) return String(value || '');
    return parsed.toISOString();
  }

  _historyCacheKey(assetType, from, to, interval?: string) {
    const iv = interval || 'auto';
    return `${assetType}:${this._normalizeHistoryIso(from)}:${this._normalizeHistoryIso(to)}:${iv}`;
  }

  _selectIntervalForRange(fromDate: Date, toDate: Date): string {
    // Fallback auto-select when user has not picked an interval
    return autoIntervalForRange(fromDate.toISOString(), toDate.toISOString());
  }

  async _fetchAssetHistory(assetType, from, to, explicitInterval?: string) {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const interval = explicitInterval || this._selectIntervalForRange(fromDate, toDate);

    const fetchers = {
      pv: () => fetchPVHistory(this.site._id, from, to, undefined, interval),
      battery: () => fetchBatteryHistory(this.site._id, from, to, undefined, interval),
      generator: () => fetchGeneratorHistory(this.site._id, from, to, undefined, interval),
      grid: () => fetchGridHistory(this.site._id, from, to, undefined, interval),
    };

    const fetcher = fetchers[assetType];
    if (!fetcher) return null;

    try {
      return await fetcher();
    } catch (error) {
      const message = String(error?.message || error || '');
      if (!message.includes('429')) throw error;
      await this._delay(600);
      return fetcher();
    }
  }

  async _getAssetHistory(assetType, from, to, interval?: string) {
    const key = this._historyCacheKey(assetType, from, to, interval);
    const now = Date.now();
    const cached = this.historyRequestCache.get(key);

    if (cached?.data && cached.expiresAt > now) {
      return cached.data;
    }

    if (cached?.promise) {
      return cached.promise;
    }

    const request = this.historyLoadChain.then(async () => {
      const data = await this._fetchAssetHistory(assetType, from, to, interval);
      this.historyRequestCache.set(key, {
        data,
        expiresAt: Date.now() + 15000,
      });
      return data;
    });

    this.historyRequestCache.set(key, {
      expiresAt: now + 15000,
      promise: request,
    });

    this.historyLoadChain = request.then(() => undefined, () => undefined);

    try {
      return await request;
    } finally {
      const latest = this.historyRequestCache.get(key);
      if (latest?.promise === request && !latest.data) {
        this.historyRequestCache.delete(key);
      }
    }
  }

  async _fetchAssetSummary(assetType) {
    const fetchers = {
      pv: () => fetchPvAssetSummary(this.site._id),
      battery: () => fetchBatteryAssetSummary(this.site._id),
      generator: () => fetchGeneratorAssetSummary(this.site._id),
      grid: () => fetchGridAssetSummary(this.site._id),
    };

    const fetcher = fetchers[assetType];
    if (!fetcher) return null;
    return fetcher();
  }

  async _getAssetSummary(assetType) {
    const key = `summary:${assetType}`;
    const now = Date.now();
    const cached = this.summaryRequestCache.get(key);

    if (cached?.data !== undefined && cached.expiresAt > now) {
      return cached.data;
    }

    if (cached?.promise) {
      return cached.promise;
    }

    const request = this._fetchAssetSummary(assetType)
      .then((data) => {
        this.summaryRequestCache.set(key, {
          data,
          expiresAt: Date.now() + 5000,
        });
        return data;
      })
      .finally(() => {
        const latest = this.summaryRequestCache.get(key);
        if (latest?.promise === request && latest.data === undefined) {
          this.summaryRequestCache.delete(key);
        }
      });

    this.summaryRequestCache.set(key, {
      expiresAt: now + 5000,
      promise: request,
    });

    return request;
  }

  _extractSummaryMetricValue(assetType, metricKey, summary) {
    const normalizedMetric = this._normalizeKey(metricKey);

    const readers = {
      pv: {
        activepower: () => summary?.activePower ?? summary?.totalOutput,
        totaloutput: () => summary?.totalOutput ?? summary?.activePower,
        reactivepower: () => summary?.reactivePower,
        dailyenergy: () => summary?.dailyEnergy,
        totalenergy: () => summary?.totalEnergy,
        availability: () => summary?.availability ?? summary?.inverterAvailability?.percent,
        irradiance: () => summary?.irradiance,
        moduletemperature: () => summary?.moduleTemperature,
      },
      battery: {
        soc: () => summary?.soc ?? summary?.averageSOC,
        averagesoc: () => summary?.averageSOC ?? summary?.soc,
        batterypower: () => summary?.batteryPower,
        activepower: () => summary?.activePower ?? summary?.batteryPower,
        reactivepower: () => summary?.reactivePower,
        chargepower: () => summary?.chargePower,
        dischargepower: () => summary?.dischargePower,
        averagetemperature: () => summary?.averageTemperature,
      },
      generator: {
        totaloutput: () => summary?.totalOutput ?? summary?.activePower,
        activepower: () => summary?.activePower,
        fuellevel: () => summary?.fuelLevel,
        rpm: () => summary?.rpm,
        oilpressure: () => summary?.oilPressure,
        coolanttemperature: () => summary?.coolantTemperature,
      },
      grid: {
        activepower: () => summary?.activePower,
        importpower: () => summary?.importPower,
        exportpower: () => summary?.exportPower,
        frequency: () => summary?.frequency,
        voltage: () => summary?.voltage,
        powerfactor: () => summary?.powerFactor,
      },
    };

    const reader = readers?.[assetType]?.[normalizedMetric];
    if (!reader) return null;

    const value = Number(reader());
    return Number.isFinite(value) ? value : null;
  }

  // Aggregation mode is now determined per-metric inside _getRegisterDictionaryKeys.

  _aggregateValues(values, mode) {
    if (!values.length) return null;
    if (mode === 'sum') {
      const sum = values.reduce((acc, current) => acc + current, 0);
      return Math.round(sum * 100) / 100;
    }
    if (mode === 'avg') {
      const avg = values.reduce((acc, current) => acc + current, 0) / values.length;
      return Math.round(avg * 100) / 100;
    }
    return values[values.length - 1];
  }

  _collectLivePayloadValues(binding, dictKeys, readings) {
    if (!Array.isArray(readings) || !readings.length) return [];
    const normalizedDictKeys = dictKeys.map((key) => String(key || '').toUpperCase());
    const dictKeySet = new Set(normalizedDictKeys);

    const byDevice = new Map();

    readings.forEach((reading) => {
      const registerId = String(reading?.registerId || '');
      if (!registerId) return;

      const register = this.registers.find((entry) => String(entry?._id || '') === registerId);

      const dictionaryKey = String(register?.dictionaryKey || reading?.dictionaryKey || reading?.registerKey || '');
      const normalizedDictionaryKey = dictionaryKey.toUpperCase();
      if (!dictKeySet.has(normalizedDictionaryKey)) return;

      const value = Number(reading?.value);
      if (!Number.isFinite(value)) return;

      const deviceId = String(reading?.deviceId || register?.deviceId || `reg:${registerId}`);
      const existing = byDevice.get(deviceId);
      const keyPriority = normalizedDictKeys.indexOf(normalizedDictionaryKey);
      const normalizedPriority = keyPriority >= 0 ? keyPriority : Number.MAX_SAFE_INTEGER;

      if (!existing || normalizedPriority < existing.priority) {
        byDevice.set(deviceId, { value, priority: normalizedPriority });
      }
    });

    return Array.from(byDevice.values())
      .map((entry) => Number(entry.value))
      .filter((value) => Number.isFinite(value));
  }

  _resolveBindingLiveValue(binding, readings, fallbackTimestamp) {
    const { keys: dictKeys, mode } = this._getRegisterDictionaryKeys(binding.assetType, binding.metricKey);
    const dictKeySet = new Set(dictKeys.map((key) => String(key || '').toUpperCase()));

    const matchingRegisters = (this.registers || []).filter((register) =>
      dictKeySet.has(String(register?.dictionaryKey || '').toUpperCase())
    );

    const valuesFromPayload = this._collectLivePayloadValues(binding, dictKeys, readings);

    const valuesFromCache = matchingRegisters
      .map((register) => this.liveRegisterCache.get(String(register?._id || '')))
      .map((entry) => Number(entry?.value))
      .filter((value) => Number.isFinite(value));

    const values = valuesFromPayload.length ? valuesFromPayload : valuesFromCache;

    const aggregated = this._aggregateValues(values, mode);
    if (!Number.isFinite(aggregated)) return null;

    const latestTsFromCache = matchingRegisters
      .map((register) => this.liveRegisterCache.get(String(register?._id || ''))?.timestamp)
      .filter(Boolean)
      .map((ts) => new Date(ts))
      .filter((date) => Number.isFinite(date.getTime()))
      .sort((a, b) => b.getTime() - a.getTime())[0];

    const resolvedTimestamp = latestTsFromCache || this._extractReadingTimestamp(null, fallbackTimestamp);
    return {
      value: aggregated,
      timestamp: resolvedTimestamp,
    };
  }

  _extractNumericReadingValue(reading) {
    const n = Number(reading?.value);
    return Number.isFinite(n) ? n : null;
  }

  _extractReadingTimestamp(reading, fallbackTimestamp?: string) {
    const raw = reading?.timestamp || fallbackTimestamp || new Date().toISOString();
    const parsed = new Date(raw);
    return Number.isFinite(parsed.getTime()) ? parsed : new Date();
  }

  _getAliasMatchScore(binding, reading) {
    const { keys: dictKeys } = this._getRegisterDictionaryKeys(binding.assetType, binding.metricKey);
    const dictKeySet = new Set(dictKeys.map((key) => String(key || '').toUpperCase()));
    const reg = this.registers.find((entry) => String(entry?._id || '') === String(reading?.registerId || ''));

    // Exact dictionaryKey match scores highest
    const candidateDk = String(reg?.dictionaryKey || reading?.dictionaryKey || reading?.registerKey || '').toUpperCase();
    if (dictKeySet.has(candidateDk)) return 3;

    // Loose name match as a low-confidence fallback
    const normalizedDictKeys = dictKeys.map((k) => this._normalizeKey(k));
    const nameHaystack = [reading?.name, reg?.name]
      .map((entry) => this._normalizeKey(entry || ''))
      .filter(Boolean);

    return nameHaystack.some((h) => normalizedDictKeys.some((k) => h.includes(k) || k.includes(h))) ? 1 : 0;
  }

  _findBindingMatchesForReading(chart, reading) {
    const bindings = Array.isArray(chart?.bindings) ? chart.bindings : [];
    const withScore = bindings
      .filter((binding) => binding?.assetType && binding?.metricKey)
      .map((binding) => ({ binding, score: this._getAliasMatchScore(binding, reading) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score);

    if (!withScore.length) return [];
    const bestScore = withScore[0].score;
    return withScore.filter((entry) => entry.score === bestScore).map((entry) => entry.binding);
  }

  _appendLivePointToTrace(trace, point, chartRangeStart: Date) {
    if (!trace?.data) trace.data = [];
    trace.data.push(point);
    trace.data = trace.data
      .filter((entry) => {
        const ts = entry?.timestamp instanceof Date ? entry.timestamp : new Date(entry?.timestamp);
        return Number.isFinite(ts.getTime()) && ts >= chartRangeStart;
      })
      .sort((a, b) => {
        const left = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
        const right = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
        return left - right;
      });

    if (trace.data.length > 2000) {
      trace.data = trace.data.slice(trace.data.length - 2000);
    }
  }

  _ingestReadingIntoRegisterCache(reading, fallbackDeviceId?: string) {
    const register = this.registers.find((entry) => String(entry?._id || '') === String(reading?.registerId || ''));
    if (!register) return;
    register.value = reading?.value;
    register.updatedAt = reading?.timestamp || new Date().toISOString();
    if (!register.deviceId && fallbackDeviceId) register.deviceId = fallbackDeviceId;

    this.liveRegisterCache.set(String(register?._id || reading?.registerId || ''), {
      value: reading?.value,
      timestamp: reading?.timestamp || new Date().toISOString(),
      registerId: String(register?._id || reading?.registerId || ''),
      deviceId: register?.deviceId || fallbackDeviceId || reading?.deviceId,
      name: register?.name || reading?.name,
    });
  }

  _intervalToMs(interval: string): number {
    const parsed = String(interval).toLowerCase();
    if (parsed.endsWith('m')) {
      const minutes = parseInt(parsed, 10);
      return Number.isFinite(minutes) ? minutes * 60 * 1000 : 15 * 60 * 1000;
    }
    if (parsed.endsWith('h')) {
      const hours = parseInt(parsed, 10);
      return Number.isFinite(hours) ? hours * 60 * 60 * 1000 : 1 * 60 * 60 * 1000;
    }
    if (parsed.endsWith('d')) {
      const days = parseInt(parsed, 10);
      return Number.isFinite(days) ? days * 24 * 60 * 60 * 1000 : 1 * 24 * 60 * 60 * 1000;
    }
    // Default fallback: 15m
    return 15 * 60 * 1000;
  }

  _setupLiveSubscription(containerId) {
    const chart = this.charts[containerId];
    if (!chart || !this.site?._id) return;

    const unsubscribe = subscribeDeviceState(this.site._id, async (payload: any) => {
      const source = String(payload?.source || '');
      const isApiPollPayload = source === 'api-poll';
      const isMqttPayload = source === 'mqtt-register' || source === 'mqtt-state';

      if (isMqttPayload) {
        chart.lastMqttPayloadAt = Date.now();
      }

      const mqttRecentlyActive = (Date.now() - Number(chart.lastMqttPayloadAt || 0)) < 20_000;
      if (isApiPollPayload && isMqttRealtimeEnabled() && (isMqttConnected() || mqttRecentlyActive)) {
        return;
      }

      // Throttle MQTT updates based on selected chart interval
      const intervalMs = this._intervalToMs(chart.interval || '15m');
      const timeSinceLastUpdate = Date.now() - Number(chart.lastMqttUpdateAt || 0);
      if (isMqttPayload && timeSinceLastUpdate < intervalMs) {
        // Skip this update; the interval window hasn't passed yet
        return;
      }
      if (isMqttPayload) {
        chart.lastMqttUpdateAt = Date.now();
      }

      const readings = this._extractReadingsFromStatePayload(payload);
      if (!readings.length) return;

      let cacheChanged = false;
      readings.forEach((reading) => {
        this._ingestReadingIntoRegisterCache(reading, payload?.deviceId);
        cacheChanged = true;
      });

      if (!cacheChanged) return;

      // Bucket the snapshot timestamp to 10 s boundaries so all bindings
      // share the same timestamp row in the merged chart data, regardless of
      // which device sent this particular MQTT message.
      const rawTs = payload?.timestamp ? new Date(payload.timestamp) : new Date();
      const bucketMs = Math.floor(rawTs.getTime() / 10000) * 10000;
      const bucketTimestamp = new Date(bucketMs);

      const assetTypes = Array.from(new Set(
        (Array.isArray(chart?.bindings) ? chart.bindings : [])
          .map((binding) => binding?.assetType)
          .filter(Boolean)
      ));

      // MQTT-first behavior:
      // - When MQTT realtime is ON and broker is connected, prefer live register values.
      // - Use REST summary only as fallback path when MQTT is OFF or disconnected.
      const shouldUseSummaryFallback = !(isMqttRealtimeEnabled() && isMqttConnected());
      const summaryMap = shouldUseSummaryFallback
        ? Object.fromEntries(
            await Promise.all(assetTypes.map(async (assetType) => [assetType, await this._getAssetSummary(assetType)]))
          )
        : {};

      let changed = false;
      const bindings = Array.isArray(chart?.bindings) ? chart.bindings : [];
      bindings.forEach((binding) => {
        if (!binding?.assetType || !binding?.metricKey) return;
        const traceName = binding.name || this._humanize(binding.metricKey);
        const trace = chart.traces.find((entry) => entry.name === traceName);
        if (!trace) return;

        const live = this._resolveBindingLiveValue(binding, readings, bucketTimestamp.toISOString());
        const summaryValue = shouldUseSummaryFallback
          ? this._extractSummaryMetricValue(
              binding.assetType,
              binding.metricKey,
              summaryMap?.[binding.assetType]
            )
          : null;

        // Prefer MQTT/register live value; summary only as fallback when live is missing.
        const liveNumeric = Number(live?.value);
        const summaryNumeric = summaryValue == null ? NaN : Number(summaryValue);
        const liveValue = Number.isFinite(liveNumeric)
          ? liveNumeric
          : (Number.isFinite(summaryNumeric) ? summaryNumeric : NaN);

        // If this metric was not present in this MQTT delta payload, carry
        // forward the previous trace value so the line continues horizontally
        // instead of "pausing" until the register changes.
        const lastPoint = Array.isArray(trace.data) && trace.data.length > 0
          ? trace.data[trace.data.length - 1]
          : null;
        const lastValue = Number(lastPoint?.value);

        // In MQTT realtime mode, do not carry stale API/history values forward.
        // Only write a point when we have a real live value for this binding.
        let nextValue = NaN;
        if (Number.isFinite(liveValue)) {
          nextValue = liveValue;
        } else if (shouldUseSummaryFallback && Number.isFinite(lastValue)) {
          // Fallback mode (MQTT off/disconnected) keeps previous behavior.
          nextValue = lastValue;
        }
        if (!Number.isFinite(nextValue)) return;

        // Upsert the bucket point: overwrite if it already exists so re-emits
        // from other devices in the same bucket update in-place.
        const rangeStart = chart.dateRange?.start || new Date(Date.now() - 48 * 3600 * 1000);
        if (!trace.data) trace.data = [];
        const existingIdx = trace.data.findIndex((p) => {
          const pts = p.timestamp instanceof Date ? p.timestamp.getTime() : new Date(p.timestamp).getTime();
          return pts === bucketMs;
        });
        if (existingIdx >= 0) {
          trace.data[existingIdx] = { timestamp: bucketTimestamp, value: nextValue };
        } else {
          this._appendLivePointToTrace(
            trace,
            { timestamp: bucketTimestamp, value: nextValue },
            rangeStart
          );
        }
        changed = true;
      });

      if (changed) {
        chart.data = buildMergedChartData(chart.traces);
        this._renderChart(containerId);
        this._saveWarmSnapshot(containerId);
      }
    });

    this.liveSubscriptions.set(containerId, unsubscribe);
  }

  _teardownLiveSubscription(containerId) {
    const unsubscribe = this.liveSubscriptions.get(containerId);
    if (!unsubscribe) return;
    try {
      unsubscribe();
    } catch (error) {
      console.warn('RechartsChartManager: failed to cleanup live subscription', error);
    }
    this.liveSubscriptions.delete(containerId);
  }

  updateChart(containerId: string, value: unknown) {
    const chart = this.charts[containerId];
    if (!chart) return;
    const now = Date.now();
    const seriesName = chart.traces?.[0]?.name || 'value';
    const numeric = Number(value);
    chart.data = [...(chart.data || []), { timestamp: now, [seriesName]: Number.isFinite(numeric) ? numeric : 0 }].slice(-500);
    this._renderChart(containerId);
  }

  cleanup() {
    this.liveSubscriptions.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch {
        // ignore
      }
    });
    this.liveSubscriptions.clear();
    this.liveRegisterCache.clear();
    this.historyRequestCache.clear();
    this.roots.forEach((root) => root.unmount());
    this.roots.clear();
    this.charts = {};
  }
}

export default RechartsChartManager;
